/**
 * Admin session context. Signed-out is a normal state (the probe answers 200 with admin null); a
 * failed probe is an explicit error, never an empty control room.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type AdminMe } from './api.ts';

type State =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'error' }
  | { status: 'ready'; me: AdminMe };

const Ctx = createContext<{
  state: State;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
} | null>(null);

const toState = (r: { admin: AdminMe | null }): State =>
  r.admin ? { status: 'ready', me: r.admin } : { status: 'anonymous' };

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const refresh = useCallback(async () => {
    try {
      setState(toState(await api.get<{ admin: AdminMe | null }>('/auth/session')));
    } catch {
      setState({ status: 'error' });
    }
  }, []);
  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setState({ status: 'anonymous' });
    }
  }, []);
  useEffect(() => {
    let live = true;
    api.get<{ admin: AdminMe | null }>('/auth/session').then(
      (r) => live && setState(toState(r)),
      () => live && setState({ status: 'error' }),
    );
    return () => {
      live = false;
    };
  }, []);
  return <Ctx.Provider value={{ state, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useAdminSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error('AdminSessionProvider missing');
  return v;
}
